import type { ChangeEvent, FunctionComponent } from 'react';

import useInputValue from '../../../hooks/useInputValue';
import LabeledTextInput from '../../generic/Forms/LabeledTextInput';

import useLicenses from './useLicenses';

type LicenseSelectorProps = {
  selectedLicense?: string;
  onSelect: (licenseId: string) => void;
  isDisabled?: boolean;
};

/**
 * A fairly generic license selector. Uses typeahead/autocomplete,
 * then renders a list of licenses which match based on that query.
 */
const LicenseSelector: FunctionComponent<LicenseSelectorProps> = ({ selectedLicense, onSelect, isDisabled }) => {
  const [searchQuery, onSearchQueryChange] = useInputValue(selectedLicense || '');
  const { licenses, status } = useLicenses(searchQuery);

  const onSelectLicense = (licenseId: string) => () => {
    onSearchQueryChange('', {} as ChangeEvent<HTMLInputElement>);
    onSelect(licenseId);
  };

  return (
    <div className="modal-add-license__selector">
      <LabeledTextInput
        labelVariant="legacy"
        label="License"
        placeholder="Select License"
        value={searchQuery || selectedLicense || ''}
        onChange={onSearchQueryChange}
        isDisabled={isDisabled}
      />
      {status.loading && (
        <div className="modal-add-license__selector__results">
          <div className="modal-add-license__selector__results__status">
            Loading...
          </div>
        </div>
      )}
      {!status.loading && licenses.length === 0 && searchQuery.length > 0 && (
        <div className="modal-add-license__selector__results">
          <div className="modal-add-license__selector__results__status">
            No licenses found matching &quot;{searchQuery}&quot;
          </div>
        </div>
      )}
      {licenses.length > 0 && (
        <ul className="modal-add-license__selector__results">
          {licenses.map((license) => (
            <li
              role="button"
              key={license.licenseId}
              className="modal-add-license__selector__results__license"
              onClick={onSelectLicense(license.licenseId)}
            >
              {license.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LicenseSelector;
